import { getOfferById } from '../../api/ApiManager';
import { GET_OFFER_BY_ID } from '../../routes/EventDetails/modules/constants';
import {
  createNewOfferLookupId,
  directToEditOffer,
  directToOfferDetails,
  flagOfferIsIncomplete,
  getOfferByIdFailure,
  getOfferByIdSuccess,
  getPriceLevelsSuccess,
  storeOfferTemplateId,
} from '../../routes/EventDetails/modules/eventDetails';
import {
  calculatePriceCodeRdat,
  fetchMissingOfferSections,
  FETCH_MISSING_OFFER_SECTIONS,
  fetchMissingOfferSectionsAbort,
  directToOfferReports,
  directToEditPriceLevelsAndCodes,
} from '../../routes/EventDetails/routes/EditOffer/modules/EditOfferModule';
import {
  alignOfferProperties,
  offerSectionsWithSelectAllEnabled,
} from '../lib/offers';
import { takeLatest, select, put } from 'redux-saga/effects';
import camelcaseKeysDeep from 'camelcase-keys-deep';
import { stringToFunctionMapping } from './constants';

//////////////////////
// Selectors for Sagas
//////////////////////
const findOffer = (state, offerId, rules) => {
  let offer;
  rules.isIncompleteOffer
    ? state.event_details.availEventOffers.forEach((off) => {
        if (Number(off.id) === Number(offerId)) {
          offer = off;
        }
      })
    : state.event_details.event.zones.forEach((zone) => {
        zone.offers.forEach((off) => {
          if (Number(off.id) === Number(offerId)) {
            offer = off;
          }
        });
      });
  return offer;
};
const findZone = (state, zoneId) => {
  return state.event_details.event.zones.filter((zone) => {
    return Number(zone.id) === Number(zoneId);
  })[0];
};
const findZoneTheOfferBelongsTo = (state, offerId) => {
  let zone;
  state.event_details.event.zones.map((z) => {
    z.offers.filter((offer) => {
      if (Number(offer.id) === Number(offerId)) {
        zone = z;
      }
    });
  });
  return zone;
};

const redirectUser = (route, eventId, offerId) => {
  let redirect;
  switch (route) {
    case 'editOffer':
      redirect = directToEditOffer(eventId, offerId);
      break;
    case 'editPrices':
      redirect = directToEditPriceLevelsAndCodes(eventId, offerId);
      break;
    case 'editDetails':
      redirect = directToOfferDetails(eventId, offerId);
      break;
    case 'reports':
      redirect = directToOfferReports(eventId, offerId);
      break;
    default:
      break;
  }
  return redirect;
};

/**
 *
 * @param { Object } action
 */
function* fetchOfferSaga(action) {
  const offerId = action.offerId;
  const globalState = yield select();
  const offer = yield select(findOffer, offerId, action.rules);
  const zone = offer.zoneId
    ? yield select(findZone, offer.zoneId)
    : yield select(findZoneTheOfferBelongsTo, offerId);

  // offer objects from offer listing do not have zoneId attached
  const isIncompleteOffer = offer.incomplete;
  const priceChart = globalState.admissions[zone.id] || null;
  const eventId = globalState.event_details.event.id;
  const eventName = globalState.event_details.event.eventName;
  const contextId = globalState.app.contextId;
  const venueId = globalState.event_details.event.venueId;
  let route = action.rules.route || 'editOffer';
  let offerName = offer.offerName;

  if (isIncompleteOffer) {
    const eventLookupId = globalState.event_details.event.lookupId;
    offerName = action.rules.offerName;
    yield put(flagOfferIsIncomplete());
    yield put(createNewOfferLookupId(eventLookupId, offer.lookupId));
    yield put(storeOfferTemplateId(offer.uniqueId));
    route = 'editDetails';
  }
  try {
    const request = getOfferById(contextId, offerId, venueId);
    const response = yield request;
    const offer = camelcaseKeysDeep(response.data.Offer);
    const offerSectionsToFetch = offerSectionsWithSelectAllEnabled(
      offer,
      priceChart,
      zone.id
    );
    const shouldGetPriceLevelsFromStore = offerSectionsToFetch.hasOwnProperty(
      'getPriceLevelsFromStore'
    );
    // once we fetch the offer, we'll know which sections are missing (select-all in bo) and need to be fetched
    if (shouldGetPriceLevelsFromStore) {
      const priceLevels =
        priceChart.priceLevels &&
        priceChart.priceLevels.result.map(
          (id) => priceChart.priceLevels.entities.priceLevel[id]
        );
      const groups = offer.groups.map((group) =>
        Object.assign({}, group, { priceLevels })
      );
      Object.assign(offer, { groups });
      yield put(getPriceLevelsSuccess(priceLevels));
    }

    const offerLabels = response.data.OfferLabels || [];
    const featuredOffers = response.data.FeaturedOffers || [];
    const isOfferPriceLevelOnSaleOffSaleDatesEnabled = !!response.data
      .IsOfferPriceLevelOnSaleOffSaleDatesEnabled;

    yield put(redirectUser(route, eventId, offerId));
    yield put(
      getOfferByIdSuccess(
        alignOfferProperties(offer, offerName),
        eventName,
        zone,
        true,
        offerLabels,
        featuredOffers,
        isOfferPriceLevelOnSaleOffSaleDatesEnabled
      )
    );
    yield put(
      fetchMissingOfferSections(
        offerSectionsToFetch.list,
        contextId,
        eventId,
        zone.id
      )
    );
    if (isIncompleteOffer) yield put(calculatePriceCodeRdat());
  } catch (err) {
    yield put(getOfferByIdFailure(err));
  }
}

export function* getMissingOfferSectionsSaga(action) {
  const { sections, contextId, eventId, zoneId } = action;
  if (!sections.length > 0) {
    yield put(fetchMissingOfferSectionsAbort());
    return;
  }

  for (let i = 0; i < sections.length; i++) {
    const curr = sections[i];
    const offerSection = stringToFunctionMapping[curr];
    const request = offerSection.fetch(contextId, eventId, zoneId);
    const response = yield request;
    const data = camelcaseKeysDeep(
      response.data.PriceChart
        ? response.data.PriceChart[curr]
        : response.data[curr]
    );
    yield put(offerSection.success(data));
    yield put(offerSection.save(data));
  }
}

/////////////////
// Watchers
/////////////////
export function* offerSagaWatcher() {
  yield takeLatest(GET_OFFER_BY_ID, fetchOfferSaga);
}
export function* offerSectionsSagaWatcher() {
  yield takeLatest(FETCH_MISSING_OFFER_SECTIONS, getMissingOfferSectionsSaga);
}



// WEBPACK FOOTER //
// ./src/common/sagas/offers.js